// lib/supabase/chat.ts

import { supabase } from '../supabase';
import { Message } from '../../types/chat';
import { formatTime } from '../utils/time';
import { storage } from './storage';

/**
 * Upload an image for a chat message
 */
export async function uploadMessageImage(file: File): Promise<string> {
  try {
    const result = await storage.uploadFile({
      bucket: 'social-feed',
      file,
    });
    
    return result.url;
  } catch (err) {
    console.error('Error uploading message image:', err);
    throw err;
  }
}

/**
 * Edit a message in a conversation
 */
export async function editMessage(messageId: string, content: string): Promise<boolean> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Not authenticated');

    // Lookup our internal user ID
    const { data: userData, error: userDataError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userDataError) throw userDataError;
    if (!userData) throw new Error('User not found');

    // Update the message
    const { error: updateError } = await supabase
      .from('messages')
      .update({ 
        content: content,
        is_edited: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', messageId)
      .eq('sender_id', userData.id); // Ensure user can only edit their own messages

    if (updateError) throw updateError;
    return true;
  } catch (err) {
    console.error('editMessage error', err);
    return false;
  }
}

/**
 * Delete a message from a conversation
 */
export async function deleteMessage(messageId: string): Promise<boolean> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Not authenticated');

    // Lookup our internal user ID
    const { data: userData, error: userDataError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userDataError) throw userDataError;
    if (!userData) throw new Error('User not found');

    // Delete the message
    const { error: deleteError } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId)
      .eq('sender_id', userData.id); // Ensure user can only delete their own messages

    if (deleteError) throw deleteError;
    return true;
  } catch (err) {
    console.error('deleteMessage error', err);
    return false;
  }
}

/**
 * Toggle like on a message (INSERT or DELETE from message_likes)
 */
export async function toggleMessageLike(messageId: string): Promise<boolean> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Not authenticated');

    // Lookup our internal user ID
    const { data: userData, error: userDataError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userDataError) throw userDataError;
    if (!userData) throw new Error('User not found');

    // See if a like already exists
    const { data: existingLike, error: likeCheckError } = await supabase
      .from('message_likes')
      .select('id')
      .eq('message_id', messageId)
      .eq('user_id', userData.id)
      .maybeSingle();
    if (likeCheckError) throw likeCheckError;

    if (existingLike) {
      // Unlike -> delete the row
      const { error: deleteError } = await supabase
        .from('message_likes')
        .delete()
        .eq('id', existingLike.id);
      if (deleteError) throw deleteError;

      // Notify clients via RPC
      await supabase.rpc('notify_message_like_status', {
        p_message_id: messageId,
        p_is_liked: false
      });
      return false;
    } else {
      // Like -> insert new row
      const { error: insertError } = await supabase
        .from('message_likes')
        .insert({ message_id: messageId, user_id: userData.id });
      if (insertError) throw insertError;

      // Notify clients via RPC
      await supabase.rpc('notify_message_like_status', {
        p_message_id: messageId,
        p_is_liked: true
      });
      return true;
    }
  } catch (err) {
    console.error('toggleMessageLike error', err);
    throw err;
  }
}

/**
 * Fetch likes count & whether the current user has liked a message
 */
export async function getMessageLikes(
  messageId: string
): Promise<{ count: number; isLiked: boolean }> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userDataError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userDataError) throw userDataError;
    if (!userData) throw new Error('User not found');

    // Total count
    const { count, error: countError } = await supabase
      .from('message_likes')
      .select('id', { count: 'exact', head: true })
      .eq('message_id', messageId);
    if (countError) throw countError;

    // Whether this user liked
    const { data: userLike, error: userLikeError } = await supabase
      .from('message_likes')
      .select('id')
      .eq('message_id', messageId)
      .eq('user_id', userData.id)
      .maybeSingle();
    if (userLikeError) throw userLikeError;

    return { count: count || 0, isLiked: !!userLike };
  } catch (err) {
    console.error('getMessageLikes error', err);
    return { count: 0, isLiked: false };
  }
}

/**
 * Subscribe to new messages (including quoted replies) in a conversation
 */
export function subscribeToMessages(
  conversationId: string,
  callback: (msg: Message) => void
) {
  const channel = supabase
    .channel(`messages:${conversationId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `conversation_id=eq.${conversationId}`
    }, async ({ new: m }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();
      if (!userData) return;

      // Fetch quoted message if it's a reply
      let quoted: Message['reply_to_message'] = undefined;
      if (m.reply_to_message_id) {
        const { data, error } = await supabase
          .from('messages')
          .select('id, content, sender_id')
          .eq('id', m.reply_to_message_id)
          .single();
        if (!error && data) quoted = data;
      }

      callback({
        id: m.id,
        content: m.content,
        time: formatTime(new Date(m.created_at)),
        isSender: m.sender_id === userData.id,
        status: m.read_at ? 'read' : 'sent',
        reply_to_message: quoted
      });
    })
    .subscribe();

  return () => supabase.removeChannel(channel);
}

/**
 * Mark a message as read in the database
 */
export async function markMessageAsRead(messageId: string): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() })
    .eq('id', messageId);
  if (error) throw error;
}

/**
 * Subscribe to like AND unlike events on message_likes in real time
 */
export function subscribeToMessageLikes(
  messageIds: string[],
  callback: (messageId: string, isLiked: boolean) => void
) {
  if (!messageIds.length) {
    return () => {};
  }

  const channel = supabase
    .channel('realtime:message_likes')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'message_likes'
    }, ({ new: p }) => {
      if (messageIds.includes(p.message_id)) {
        callback(p.message_id, true);
      }
    })
    .on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'message_likes'
    }, ({ old }) => {
      if (messageIds.includes(old.message_id)) {
        callback(old.message_id, false);
      }
    })
    .subscribe();

  return () => supabase.removeChannel(channel);
}
